import React, { useId, useState } from 'react';
import {
  Activity,
  Bell,
  Box,
  Cable,
  Circle,
  Cpu,
  Flag,
  HardDrive,
  Heart,
  Key,
  Layers,
  Lightbulb,
  Monitor,
  Network,
  Plug,
  Power,
  Radio,
  Settings,
  Shield,
  Star,
  Terminal,
  Usb,
  Volume2,
  Wifi,
  Zap,
  EthernetPort,
  type LucideIcon,
} from 'lucide-react';
import { SimpleLayout, PortRect } from '../../types';
import {
  PORT,
  layoutPortKey,
  inferLayoutGrid,
  editorStageSize,
  editorCellPosition,
  EDITOR_CELL_SIZE,
  EDITOR_EMPTY_CELL,
  EDITOR_PANEL_BG,
} from '../../utils/portLayout';
import {
  loadCustomPortTypes,
  portKindColor,
  portKindDisplayName,
  portKindIconName,
} from '../../utils/customPortTypes';
import { poeRj45Look, portHoverTooltip, shadeHex } from '../../utils/poeRj45Look';
import { PoeRj45Marks } from './PoeRj45Marks';
import { PortHoverTooltip } from './PortHoverTooltip';

const DETAIL_ICONS: Record<string, LucideIcon> = {
  Power,
  Zap,
  Usb,
  Terminal,
  Monitor,
  Volume2,
  Radio,
  Box,
  Key,
  Activity,
  Bell,
  Layers,
  Circle,
  Cpu,
  HardDrive,
  Network,
  Wifi,
  Cable,
  Plug,
  EthernetPort,
  Lightbulb,
  Settings,
  Flag,
  Shield,
  Star,
  Heart,
};

const MOVE_PREVIEW_COLOR = '#eab308';
const MOVE_PREVIEW_DIP = 12;

function tileRectForPort(
  port: PortRect,
  matchEditor: boolean,
  editorGrid: ReturnType<typeof inferLayoutGrid> | null,
): { x: number; y: number; width: number; height: number } {
  if (matchEditor && editorGrid) {
    const key = layoutPortKey(port);
    const cell = editorGrid.cells.find((c) => c.port && layoutPortKey(c.port) === key);
    if (cell) {
      const pos = editorCellPosition(cell.col, cell.row);
      return { x: pos.x, y: pos.y, width: EDITOR_CELL_SIZE, height: EDITOR_CELL_SIZE };
    }
  }
  return { x: port.x, y: port.y, width: PORT.width, height: PORT.height };
}

function movePreviewPath(
  from: { x: number; y: number; width: number; height: number },
  to: { x: number; y: number; width: number; height: number },
): string {
  const x1 = from.x + from.width / 2;
  const y1 = from.y + from.height;
  const x2 = to.x + to.width / 2;
  const y2 = to.y + to.height;
  const railY = Math.max(y1, y2) + MOVE_PREVIEW_DIP;
  return `M ${x1} ${y1} L ${x1} ${railY} L ${x2} ${railY} L ${x2} ${y2}`;
}

function PortStageComponent({
  layout,
  highlightId,
  onPortMouseDown,
  className,
  style,
  activePortNumbers,
  activePortIds,
  onPortClick,
  onPortHover,
  hoveredPort,
  hoveredPortId,
  selectedPort,
  selectedPortId,
  portCustomLabels,
  colourizePorts = true,
  hasGlow = false,
  hideNumbers = false,
  showChrome = true,
  showTypeLabel = true,
  showTypeIcon = true,
  showConnectedRing = true,
  hoverAccent,
  detailView = false,
  matchEditor = false,
  portCaptions,
  getPortTooltip,
  showMovePreview = false,
}: {
  layout: SimpleLayout;
  highlightId?: string;
  onPortMouseDown?: (e: React.MouseEvent, id: string) => void;
  className?: string;
  style?: React.CSSProperties;
  activePortNumbers?: number[];
  activePortIds?: string[];
  onPortClick?: (portNumber: number, kind?: string) => void;
  onPortHover?: (portNumber: number | null, port?: PortRect) => void;
  hoveredPort?: number | null;
  hoveredPortId?: string | null;
  selectedPort?: number | null;
  selectedPortId?: string | null;
  portCustomLabels?: Record<number, string>;
  colourizePorts?: boolean;
  hasGlow?: boolean;
  hideNumbers?: boolean;
  /** When false, hide type icons and text (colour + outline + PoE pips only). */
  showChrome?: boolean;
  /** When false, keep the type icon but hide the kind label (e.g. RJ45). */
  showTypeLabel?: boolean;
  /** When false, hide the type icon inside editor tiles. */
  showTypeIcon?: boolean;
  /** When false, connected editor tiles use fill only (no blue ring). */
  showConnectedRing?: boolean;
  hoverAccent?: 'yellow';
  detailView?: boolean;
  /** Canvas trial: same tiles as the layout editor, plus number bands above/below. */
  matchEditor?: boolean;
  /** Logic captions keyed by layoutPortKey, e.g. "at 15W". */
  portCaptions?: Record<string, string>;
  getPortTooltip?: (port: PortRect, connected: boolean) => { title: string; lines: string[] };
  /** Yellow U-arrow from the selected port to a hovered open port (cable move preview). */
  showMovePreview?: boolean;
}) {
  const { stage, ports } = layout;
  const activeNumberSet = new Set(activePortNumbers || []);
  const activeIdSet = new Set(activePortIds || []);
  const useActiveIds = activePortIds != null;
  const editorTile = matchEditor || detailView;
  const customTypes = editorTile ? loadCustomPortTypes() : {};
  const editorGrid = matchEditor ? inferLayoutGrid(layout) : null;
  const editorSize = editorGrid ? editorStageSize(editorGrid.cols, editorGrid.rows) : null;
  const [tooltip, setTooltip] = useState<{ title: string; lines: string[]; x: number; y: number } | null>(null);
  const markerId = `port-move-arrow-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const panelStyle: React.CSSProperties = matchEditor
    ? {
        position: 'relative',
        width: editorSize?.width ?? stage.width,
        height: editorSize?.height ?? stage.height,
        background: EDITOR_PANEL_BG,
        border: '1px solid #475569',
        borderRadius: 6,
        overflow: 'visible',
        boxShadow: hasGlow
          ? '0 0 12px rgba(59, 130, 246, 0.5), 0 0 24px rgba(59, 130, 246, 0.3), 0 0 36px rgba(59, 130, 246, 0.2)'
          : undefined,
        transition: hasGlow ? 'box-shadow 0.3s ease-in-out' : undefined,
        ...style,
        ...(style?.background ? {} : { background: EDITOR_PANEL_BG }),
      }
    : {
        position: 'relative',
        width: stage.width,
        height: stage.height,
        background: '#e5e7eb',
        border: '1px solid #9ca3af',
        borderRadius: 6,
        boxShadow: hasGlow
          ? '0 0 12px rgba(59, 130, 246, 0.5), 0 0 24px rgba(59, 130, 246, 0.3), 0 0 36px rgba(59, 130, 246, 0.2)'
          : undefined,
        transition: hasGlow ? 'box-shadow 0.3s ease-in-out' : undefined,
        ...style,
      };

  const renderPortTile = (
    p: PortRect,
    pos: { x: number; y: number; width: number; height: number },
    useEditorTile: boolean,
  ) => {
    const portKey = layoutPortKey(p);
    const isHighlighted = highlightId === p.id || highlightId === portKey;
    const isSelected = selectedPortId != null ? selectedPortId === portKey : selectedPort === p.number;
    const isHovered = hoveredPortId != null ? hoveredPortId === portKey : hoveredPort === p.number;
    const isActive = useActiveIds ? activeIdSet.has(portKey) : activeNumberSet.has(p.number);
    const customLabel = portCustomLabels?.[p.number]?.trim() || '';
    const schemeName = p.label || String(p.number);
    const typeLabel = useEditorTile ? portKindDisplayName(p.kind, customTypes) : '';
    const typeColor = useEditorTile && colourizePorts ? portKindColor(p.kind, customTypes) : undefined;
    const fillColor = typeColor
      ? (useActiveIds && !isActive ? shadeHex(typeColor, 0.5) : typeColor)
      : undefined;
    const iconName = useEditorTile ? portKindIconName(p.kind, customTypes) : undefined;
    const DetailIcon = iconName ? DETAIL_ICONS[iconName] || Box : null;
    const logicCaption = portCaptions?.[portKey];
    const poeLook = poeRj45Look(p);
    const isRj45 = String(p.kind) === 'rj45';
    const poeClass = isRj45 && poeLook !== 'data' ? poeLook : '';
    const showLabels = showChrome && !hideNumbers;
    return (
      <div
        key={portKey}
        className={[
          'port',
          useEditorTile ? 'port-detail' : '',
          matchEditor ? 'port-editor' : '',
          !useEditorTile ? p.kind : '',
          poeClass,
          p.flipped ? 'flipped' : '',
          isActive && !useEditorTile ? 'active' : '',
          isActive && useEditorTile && showConnectedRing ? 'port-detail-connected' : '',
          useActiveIds && !isActive ? 'port-unconnected' : '',
          isHighlighted ? 'ring-2 ring-blue-500' : '',
          (isSelected || (isHovered && hoverAccent !== 'yellow')) && isActive && !useEditorTile ? 'selected' : '',
          (isHovered && hoverAccent !== 'yellow') || (isSelected && !useEditorTile) ? 'hovered' : '',
          isHovered && hoverAccent === 'yellow' ? 'port-hover-yellow' : '',
          isSelected && useEditorTile ? 'port-detail-endpoint' : '',
        ].join(' ')}
        style={{
          left: pos.x,
          top: pos.y,
          width: pos.width,
          height: pos.height,
          position: 'absolute',
          cursor: onPortMouseDown || onPortClick ? 'pointer' : 'default',
          background: useEditorTile
            ? (colourizePorts ? fillColor : (useActiveIds && !isActive ? '#4b5563' : '#9ca3af'))
            : (isHighlighted ? 'rgba(59, 130, 246, 0.3)' : undefined),
          borderColor: !colourizePorts && !useEditorTile ? '#9ca3af' : undefined,
        }}
        data-port-id={portKey}
        data-port-number={p.number}
        data-port-kind={p.kind || ''}
        onMouseDown={onPortMouseDown ? (e) => { e.stopPropagation(); onPortMouseDown(e, p.id); } : onPortClick ? (e) => { e.stopPropagation(); } : undefined}
        onClick={onPortClick ? (e) => { e.stopPropagation(); onPortClick(p.number, p.kind); } : undefined}
        onMouseEnter={(e) => {
          if (e.buttons !== 0) return;
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          const info = getPortTooltip
            ? getPortTooltip(p, useActiveIds ? isActive : false)
            : portHoverTooltip(p, useActiveIds ? isActive : false);
          setTooltip({ ...info, x: r.left + r.width / 2, y: r.top });
          onPortHover?.(p.number, p);
        }}
        onMouseLeave={() => {
          setTooltip(null);
          onPortHover?.(null);
        }}
      >
        {poeLook !== 'data' ? <PoeRj45Marks look={poeLook} /> : null}
        {useEditorTile && showChrome && (showTypeIcon || showTypeLabel) ? (
          <div className="port-detail-inner">
            {showTypeIcon && DetailIcon ? <DetailIcon size={matchEditor ? 14 : 8} strokeWidth={2.25} /> : null}
            {showTypeLabel ? <span className="port-detail-label">{typeLabel}</span> : null}
          </div>
        ) : null}
        {showLabels ? (
          <div
            className={`number${(schemeName.length > 4) ? ' number-long' : ''}${customLabel || logicCaption ? ' has-custom' : ''}`}
            data-port-number-label
          >
            <span>{schemeName}</span>
            {customLabel ? (
              <span className="port-custom-label">{customLabel}</span>
            ) : logicCaption ? (
              <span className="port-custom-label">{logicCaption}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  let movePreviewD: string | null = null;
  let movePreviewSwap = false;
  if (showMovePreview && selectedPortId && hoveredPortId && selectedPortId !== hoveredPortId) {
    const fromPort = ports.find((p) => layoutPortKey(p) === selectedPortId);
    const toPort = ports.find((p) => layoutPortKey(p) === hoveredPortId);
    if (fromPort && toPort) {
      const toKey = layoutPortKey(toPort);
      movePreviewSwap = useActiveIds ? activeIdSet.has(toKey) : activeNumberSet.has(toPort.number);
      movePreviewD = movePreviewPath(
        tileRectForPort(fromPort, matchEditor, editorGrid),
        tileRectForPort(toPort, matchEditor, editorGrid),
      );
    }
  }

  return (
    <div className={className} style={panelStyle}>
      {matchEditor && editorGrid ? (
        <>
          {editorGrid.cells.map((cell) => {
            const pos = editorCellPosition(cell.col, cell.row);
            if (cell.blank) {
              return null;
            }
            if (!cell.port) {
              return (
                <div
                  key={`empty-${cell.row}-${cell.col}`}
                  className="port-editor-empty"
                  style={{
                    left: pos.x,
                    top: pos.y,
                    width: EDITOR_CELL_SIZE,
                    height: EDITOR_CELL_SIZE,
                    background: EDITOR_EMPTY_CELL,
                  }}
                />
              );
            }
            return renderPortTile(
              cell.port,
              { ...pos, width: EDITOR_CELL_SIZE, height: EDITOR_CELL_SIZE },
              true,
            );
          })}
        </>
      ) : (
        ports.map((p) =>
          renderPortTile(
            p,
            { x: p.x, y: p.y, width: PORT.width, height: PORT.height },
            detailView,
          )
        )
      )}
      {movePreviewD ? (
        <svg className="port-move-preview" aria-hidden="true">
          <defs>
            <marker
              id={`${markerId}-end`}
              markerWidth="8"
              markerHeight="8"
              refX="7"
              refY="4"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <path d="M 0 0.5 L 8 4 L 0 7.5 Z" fill={MOVE_PREVIEW_COLOR} />
            </marker>
            {movePreviewSwap ? (
              <marker
                id={`${markerId}-start`}
                markerWidth="8"
                markerHeight="8"
                refX="7"
                refY="4"
                orient="auto-start-reverse"
                markerUnits="userSpaceOnUse"
              >
                <path d="M 0 0.5 L 8 4 L 0 7.5 Z" fill={MOVE_PREVIEW_COLOR} />
              </marker>
            ) : null}
          </defs>
          <path
            d={movePreviewD}
            fill="none"
            stroke={MOVE_PREVIEW_COLOR}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            markerStart={movePreviewSwap ? `url(#${markerId}-start)` : undefined}
            markerEnd={`url(#${markerId}-end)`}
          />
        </svg>
      ) : null}
      {tooltip ? <PortHoverTooltip title={tooltip.title} lines={tooltip.lines} x={tooltip.x} y={tooltip.y} /> : null}
    </div>
  );
}

export const PortStage = React.memo(PortStageComponent);
